import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** County branding (global_variable whitelist) for documents the portal renders. */
export interface Branding {
  countyName: string;          // "Putnam County Tax Collector" style display name
  certCountyName: string;      // county only, e.g. "Putnam"
  taxCollectorName: string;
  certTaxCollectorName: string;
  phone: string;
  email: string;
  streetAddress: string;
  website: string;
  /** Tax collector mailing address (street + suite + city/state/zip) for mail-in payments. */
  mailingAddress: string[];
  logo: string;                // data:image/... uploaded in TDT-Internal Global Variables, else ''
  signature: string;
}

// Legacy rows hold bare file names ("logo.jpeg") that nothing serves; only uploaded images count.
const img = (v?: string) => (v ?? '').startsWith('data:image/') ? v! : '';

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private cache$?: Observable<Branding>;

  constructor(private http: HttpClient) {}

  get(): Observable<Branding> {
    if (!this.cache$) {
      this.cache$ = this.http.get<Record<string, string>>(`${environment.apiUrl}/external/branding`).pipe(
        map(v => ({
          countyName: v['county_name'] ?? '',
          certCountyName: v['cert_county_name'] || v['county_name'] || '',
          taxCollectorName: v['tax_collector_name'] ?? '',
          certTaxCollectorName: v['cert_tax_collector_name'] || v['tax_collector_name'] || '',
          phone: v['tax_collector_phone'] ?? '',
          email: v['tax_group_email'] ?? '',
          streetAddress: v['street_address'] ?? '',
          website: v['website_url'] ?? '',
          mailingAddress: [
            [v['street_address'], v['apt_suite']].filter(Boolean).join(', '),
            [v['city'], [v['state'], v['zip_code']].filter(Boolean).join(' ')].filter(Boolean).join(', '),
          ].filter(Boolean),
          logo: img(v['platform_logo']),
          signature: img(v['signature_image']),
        })),
        catchError(() => of({ countyName: '', certCountyName: '', taxCollectorName: '', certTaxCollectorName: '', phone: '', email: '', streetAddress: '', website: '', mailingAddress: [], logo: '', signature: '' })),
        shareReplay(1)
      );
    }
    return this.cache$;
  }
}
